// src/services/commercesServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/commerces`;

/* =========================
   Helpers
   ========================= */
// Récupère le token à chaque appel (utile après login/logout)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Construit une querystring propre
const buildQuery = (params = {}) => {
  const q = new URLSearchParams();

  Object.entries(params).forEach(([key, val]) => {
    if (val === undefined || val === null) return;

    // Tableaux -> "a,b,c" (ex: categorie)
    if (Array.isArray(val)) {
      const joined = val
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(",");
      if (joined) q.append(key, joined);
      return;
    }

    // Booléens -> "true"/"false"
    if (typeof val === "boolean") {
      q.append(key, val ? "true" : "false");
      return;
    }

    // Autres types
    const str = String(val).trim();
    if (str !== "") q.append(key, str);
  });

  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

// URL absolue (utile si /uploads est servi par le back)
export const toAbsoluteUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (url.startsWith("/uploads/") || url.startsWith("uploads/")) {
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  }
  return url;
};

export const toAbsoluteLogoUrl = toAbsoluteUrl;
export const toAbsolutePhotoUrl = toAbsoluteUrl;

/* =========================
   API — Public
   ========================= */

// GET — Liste simple (actifs, tri alpha) pour préchargement
export const getAllCommerces = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllCommerces`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des commerces :", error);
    return [];
  }
};

// GET — Catégories distinctes (pour filtres)
export const getDistinctCategories = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/categories`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data.filter(Boolean) : [];
  } catch (error) {
    console.error("Erreur chargement catégories commerces :", error);
    return [];
  }
};

// GET — Recherche + filtres + pagination
// params: { q, categorie: string[]|string, ville, code_postal,
//           is_active, has_logo, has_photo, with_gps,
//           page, limit, sort, dir, lat, lng }
export const searchCommerces = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });

    // Normalisation du payload
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (error) {
    console.error("Erreur recherche commerces :", error);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

// GET — Détail
export const getCommerceById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur chargement commerce :", error);
    return null;
  }
};

/* =========================
   API — Géolocalisation (carte)
   ========================= */

// GET — Dans une bbox: params { north, south, east, west, only_active, limit }
export const getCommercesWithinBbox = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/bbox${qs}`, {
      headers: authHeaders(),
    });
    return Array.isArray(data?.items) ? data.items : [];
  } catch (error) {
    console.error("Erreur chargement commerces bbox :", error);
    return [];
  }
};

// GET — Proches d’un point: params { lat, lng, radius_km, only_active, limit }
export const getCommercesNear = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/near${qs}`, {
      headers: authHeaders(),
    });
    return Array.isArray(data?.items) ? data.items : [];
  } catch (error) {
    console.error("Erreur chargement commerces (near) :", error);
    return [];
  }
};

// GET — Détail par slug
export const getCommerceBySlug = async (slug) => {
  if (!slug) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/by-slug/${encodeURIComponent(slug)}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur getCommerceBySlug :", error);
    return null;
  }
};

/* =========================
   API — Admin (CRUD)
   ========================= */

// POST — Création
export const createCommerce = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur création commerce :", error);
    return null;
  }
};

// PUT — Remplacement total
export const updateCommerce = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur mise à jour commerce :", error);
    return null;
  }
};

// PATCH — Mise à jour partielle
export const patchCommerce = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur patch commerce :", error);
    return null;
  }
};

// PATCH — Activer/Désactiver
export const toggleCommerceActive = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}/toggle`, null, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur toggle commerce :", error);
    return null;
  }
};

// DELETE — Suppression
export const deleteCommerce = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return true;
  } catch (error) {
    console.error("Erreur suppression commerce :", error);
    return false;
  }
};
